/* src/pages/HousekeepingReportsPage.js */

import React, { useState, useEffect } from 'react';
import { Alert, Button, Modal, Table } from 'antd';
import moment from 'moment';

import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosInstance';
import {
  CardTitle,
  PageHeader,
  SectionCard,
  StatStrip,
  StatusTag,
  TabBar,
  fmtDate,
  fmtDateTime,
  tablePagination,
  useFlatPage
} from '../components/ui';
import { IconRefresh } from '../components/ui/icons';
import notify from "../utils/notify";

const HousekeepingReportsPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;
  // The diagnostic endpoints are SystemAdmin-only on the API; everyone else just gets the daily report.
  const isSystemAdmin = String(authData?.role || '').toLowerCase() === 'systemadmin';

  const [deepDiagnosticData, setDeepDiagnosticData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [error, setError] = useState(null);
  const [showFixModal, setShowFixModal] = useState(false);
  const [tab, setTab] = useState('report');

  // Summary data from GET /api/HousekeepingReport
  const [reportSummary, setReportSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // ---- API calls ----------------------------------------------------------

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      // HotelId comes from claims on the API; we just call the endpoint.
      const response = await axiosInstance.get('/HousekeepingReport');
      setReportSummary(response.data);
    } catch (err) {
      console.error('Summary load failed:', err);
      notify.error({
        title: 'Failed to load housekeeping summary',
        description: err.response?.data?.message || err.message
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  const runDeepDiagnostic = async () => {
    if (!hotelId) {
      notify.error({
        title: 'No Hotel ID',
        description: 'Hotel ID is missing'
      });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        '/Housekeepingreport/Report/deep-diagnostic',
        {
          params: { hotelId }
        }
      );

      setDeepDiagnosticData(response.data);
      notify.success({
        title: 'Deep Diagnostic Completed',
        description: `Found ${response.data.taskAnalysis?.totalTasks || 0} tasks`
      });
    } catch (err) {
      console.error('Deep diagnostic failed:', err);
      setError(err.response?.data || err.message);
      notify.error({
        title: 'Deep Diagnostic Failed',
        description: 'Could not analyze data relationships'
      });
    } finally {
      setLoading(false);
    }
  };

  const fixDataIssues = async () => {
    if (!hotelId) return;

    setFixing(true);
    try {
      const response = await axiosInstance.post(
        '/Housekeepingreport/Report/fix-data-issues',
        null,
        {
          params: { hotelId }
        }
      );

      notify.success({
        title: 'Data Issues Fixed',
        description: `Fixed ${response.data.fixedCount} issues`
      });

      // Refresh diagnostic data
      runDeepDiagnostic();
      setShowFixModal(false);
    } catch (err) {
      console.error('Fix failed:', err);
      notify.error({
        title: 'Fix Failed',
        description: err.response?.data?.error || 'Could not fix data issues'
      });
    } finally {
      setFixing(false);
    }
  };

  const refreshAll = () => {
    loadSummary();
    if (isSystemAdmin) runDeepDiagnostic();
  };

  useEffect(() => {
    if (hotelId) {
      loadSummary();
      if (isSystemAdmin) runDeepDiagnostic();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the listed values change; the function is re-created every render and is not a trigger
  }, [hotelId, isSystemAdmin]);

  if (!hotelId) {
    return (
      <Alert
        title="Hotel ID Missing"
        description="Please ensure you are logged in and have a valid hotel ID."
        type="error"
        showIcon
      />
    );
  }

  const daily = reportSummary?.dailySummary;
  const staffPerformance = reportSummary?.staffPerformance || [];

  // Detailed list of rooms that were cleaned (from diagnostic sample tasks)
  const cleanedTasks =
    deepDiagnosticData?.taskAnalysis?.sampleTasks?.filter(
      (task) => task.Status === 'Completed'
    ) || [];

  const ta = deepDiagnosticData?.taskAnalysis;
  const totalTasks = ta?.totalTasks || 0;
  const health = (n) => (n === totalTasks ? 'good' : 'bad');
  const issueCount = (k) => deepDiagnosticData?.issues?.[k]?.length || 0;
  const mismatches = issueCount('roomHotelMismatch') + issueCount('userHotelMismatch');
  const busy = loading || summaryLoading;

  const staffColumns = [
    { title: 'Staff', dataIndex: 'staffName', key: 'staffName', render: (v) => <span className="hms-cell-main">{v}</span> },
    { title: 'Rooms cleaned', dataIndex: 'roomsCleaned', key: 'roomsCleaned', align: 'right' },
    {
      title: 'Time spent',
      dataIndex: 'timeSpentHours',
      key: 'timeSpentHours',
      align: 'right',
      render: (v) => `${v ? Number(v).toFixed(2) : 0} hrs`
    },
    { title: 'Issues reported', dataIndex: 'issuesReported', key: 'issuesReported', align: 'right' }
  ];

  const cleanedColumns = [
    { title: 'Room', dataIndex: 'RoomNumber', key: 'RoomNumber', render: (v) => <span className="hms-cell-main">{v}</span> },
    { title: 'Cleaned by', dataIndex: 'UserName', key: 'UserName' },
    {
      title: 'Completed',
      dataIndex: 'CompletedAt',
      key: 'CompletedAt',
      render: (v) => (v ? fmtDateTime(v) : <span className="hms-muted">—</span>)
    },
    {
      title: 'Duration',
      key: 'duration',
      align: 'right',
      render: (_, task) =>
        task.CompletedAt && task.AssignedAt
          ? `${Math.max(0, moment(task.CompletedAt).diff(moment(task.AssignedAt), 'minutes'))} min`
          : '—'
    }
  ];

  const sampleColumns = [
    { title: 'Task', dataIndex: 'TaskId', key: 'TaskId', width: 90, render: (v) => <span className="hms-cell-main">#{v}</span> },
    { title: 'Status', dataIndex: 'Status', key: 'Status', width: 140, render: (s) => <StatusTag status={s}>{s === 'InProgress' ? 'In progress' : s}</StatusTag> },
    { title: 'Assigned', dataIndex: 'AssignedAt', key: 'AssignedAt', width: 130, render: (v) => (v ? fmtDate(v) : '—') },
    {
      title: 'Room',
      key: 'room',
      render: (_, task) => (
        <StatusTag status={task.RoomExists ? 'available' : 'cancelled'}>
          {task.RoomNumber || 'None'}
          {!task.RoomExists && task.RoomId ? ` (ID ${task.RoomId})` : ''}
        </StatusTag>
      )
    },
    {
      title: 'Staff',
      key: 'staff',
      render: (_, task) => (
        <StatusTag status={task.UserExists ? 'available' : 'cancelled'}>
          {task.UserName || 'None'}
          {!task.UserExists && task.UserId ? ` (ID ${task.UserId})` : ''}
        </StatusTag>
      )
    },
    {
      title: 'Hotel ID',
      key: 'hotel',
      width: 110,
      render: (_, task) => <StatusTag status={task.HotelId === hotelId ? 'available' : 'cancelled'}>{task.HotelId}</StatusTag>
    }
  ];

  return (
    <div className="hms-page hk-page">
      <PageHeader
        actions={
          <Button icon={<IconRefresh />} loading={busy} onClick={refreshAll}>
            Refresh
          </Button>
        }
      />

      <SectionCard flush>
        <TabBar
          tabs={[
            { key: 'report', label: 'Daily report' },
            ...(isSystemAdmin
              ? [{ key: 'health', label: 'Data health', count: issueCount('missingRooms') + issueCount('missingUsers') + mismatches }]
              : [])
          ]}
          value={tab}
          onChange={setTab}
        />
      </SectionCard>

      {error && (
        <Alert
          style={{ marginTop: 16 }}
          title="Diagnostic error"
          description={<pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(error, null, 2)}</pre>}
          type="error"
          showIcon
        />
      )}

      {tab === 'report' && (
        <div className="hms-stack" style={{ marginTop: 16 }}>
          <SectionCard flush title="Operational summary">
            <StatStrip
              items={[
                { label: 'Rooms cleaned', value: daily?.roomsCleaned || 0, tone: 'good' },
                { label: 'Rooms pending', value: daily?.roomsPending || 0, tone: 'warn' },
                { label: 'Under maintenance / in progress', value: daily?.roomsUnderMaintenance || 0, tone: 'bad' }
              ]}
            />
          </SectionCard>

          <SectionCard flush title={<CardTitle count={staffPerformance.length}>Staff performance</CardTitle>}>
            <Table
              className="hms-table"
              rowKey={(r) => r.staffName}
              columns={staffColumns}
              dataSource={staffPerformance}
              loading={summaryLoading}
              pagination={tablePagination('staff member', { hideOnSinglePage: true })}
              locale={{ emptyText: <div className="hms-empty">No staff performance data for this period</div> }}
            />
          </SectionCard>

          {isSystemAdmin && (
            <SectionCard flush title={<CardTitle count={cleanedTasks.length}>Rooms cleaned</CardTitle>}>
              <Table
                className="hms-table"
                rowKey={(r) => r.TaskId}
                columns={cleanedColumns}
                dataSource={cleanedTasks}
                loading={loading}
                pagination={tablePagination('room', { hideOnSinglePage: true })}
                locale={{ emptyText: <div className="hms-empty">No completed rooms in the current data sample</div> }}
              />
            </SectionCard>
          )}
        </div>
      )}

      {isSystemAdmin && tab === 'health' && (
        <div className="hms-stack" style={{ marginTop: 16 }}>
          <SectionCard flush title="Data health">
            <StatStrip
              items={[
                { label: 'Total tasks', value: totalTasks },
                { label: 'Tasks with rooms', value: ta?.tasksWithRoom || 0, suffix: `of ${totalTasks}`, tone: health(ta?.tasksWithRoom) },
                { label: 'Tasks with staff', value: ta?.tasksWithUser || 0, suffix: `of ${totalTasks}`, tone: health(ta?.tasksWithUser) },
                {
                  label: 'Complete relationships',
                  value: ta?.tasksWithBothRelationships || 0,
                  suffix: `of ${totalTasks}`,
                  tone: health(ta?.tasksWithBothRelationships)
                }
              ]}
            />
          </SectionCard>

          <SectionCard
            flush
            title="Identified issues"
            extra={
              <Button danger loading={fixing} onClick={() => setShowFixModal(true)}>
                Fix data issues
              </Button>
            }
          >
            <StatStrip
              items={[
                { label: 'Missing room links', value: issueCount('missingRooms'), tone: issueCount('missingRooms') ? 'bad' : 'good' },
                { label: 'Missing staff links', value: issueCount('missingUsers'), tone: issueCount('missingUsers') ? 'bad' : 'good' },
                { label: 'Hotel ID mismatches', value: mismatches, tone: mismatches ? 'warn' : 'good' }
              ]}
            />
          </SectionCard>

          <SectionCard flush title={<CardTitle count={ta?.sampleTasks?.length || 0}>Task sample</CardTitle>}>
            <Table
              className="hms-table"
              rowKey={(r) => r.TaskId}
              columns={sampleColumns}
              dataSource={ta?.sampleTasks || []}
              loading={loading}
              pagination={tablePagination('task', { hideOnSinglePage: true })}
              scroll={{ x: 800 }}
              rowClassName={(r) => (r.RoomExists && r.UserExists ? '' : 'row-highlight')}
              locale={{ emptyText: <div className="hms-empty">No housekeeping tasks found for this hotel</div> }}
            />
          </SectionCard>

          <div className="hms-grid-2">
            <SectionCard flush title={<CardTitle count={deepDiagnosticData?.availableRooms?.length || 0}>Available rooms</CardTitle>}>
              <Table
                className="hms-table"
                rowKey={(r) => r.RoomId}
                columns={[
                  { title: 'Room', dataIndex: 'RoomNumber', key: 'RoomNumber', render: (v) => <span className="hms-cell-main">{v}</span> },
                  { title: 'ID', dataIndex: 'RoomId', key: 'RoomId', align: 'right' }
                ]}
                dataSource={deepDiagnosticData?.availableRooms || []}
                pagination={tablePagination('room', { pageSize: 5, showSizeChanger: false, hideOnSinglePage: true })}
                locale={{ emptyText: <div className="hms-empty">No rooms found</div> }}
              />
            </SectionCard>

            <SectionCard flush title={<CardTitle count={deepDiagnosticData?.availableUsers?.length || 0}>Available staff</CardTitle>}>
              <Table
                className="hms-table"
                rowKey={(r) => r.Id}
                columns={[
                  { title: 'Staff', key: 'name', render: (_, u) => <span className="hms-cell-main">{u.FirstName} {u.LastName}</span> },
                  { title: 'ID', dataIndex: 'Id', key: 'Id', align: 'right' }
                ]}
                dataSource={deepDiagnosticData?.availableUsers || []}
                pagination={tablePagination('staff member', { pageSize: 5, showSizeChanger: false, hideOnSinglePage: true })}
                locale={{ emptyText: <div className="hms-empty">No staff found</div> }}
              />
            </SectionCard>
          </div>
        </div>
      )}

      <Modal
        title="Fix data issues"
        open={showFixModal}
        onOk={fixDataIssues}
        onCancel={() => setShowFixModal(false)}
        confirmLoading={fixing}
        okText="Fix issues"
        okType="danger"
        width={520}
        destroyOnHidden
      >
        <Alert
          title="This changes your data"
          description="It repairs missing room and staff links by assigning available rooms and staff to those tasks. Review the Data health tab first; this cannot be undone."
          type="warning"
          showIcon
        />
        <p className="hms-modal-note" style={{ marginTop: 16, marginBottom: 6 }}>
          This will fix:
        </p>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <li>Tasks without a room link</li>
          <li>Tasks without a staff link</li>
          <li>Fallback rooms and staff are assigned where needed</li>
        </ul>
      </Modal>
    </div>
  );
};

export default HousekeepingReportsPage;
